import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ApiURL } from "../../setting/GlobleVariable";
import { useInstaPassAIChat } from "./InstaPassAIChatContext";
import { trackEvent } from "../../utils/googleAnalytics";
import { trackMeta } from "../../utils/metaPixel";

/**
 * First screen for people arriving from a paid ad (TikTok, Meta, YouTube).
 *
 * Paid visitors were leaving after ~1 second: the homepage hero was a
 * 4–26 MB video (a black box on a phone) and the price read "From —"
 * because it downloaded every listing to find the cheapest. This screen
 * shows what the ad promised straight away: the poster, both dates with a
 * real "from" price, the promo code, and Hailey — in about 60 KB.
 *
 * Campaign: JAY-Z at SoFi Stadium. It switches itself off once both
 * shows have passed, and the normal homepage takes over.
 */
export const CAMPAIGN = {
  key: "jayz-sofi-2026",
  artist: "JAY-Z",
  venue: "SoFi Stadium",
  city: "Inglewood, CA",
  poster: "/media/jayz-sofi-poster.jpg",
  promo: "SAVE NOW",
  promoDiscount: "5% off",
  haileyPhone: "+18777649917",
  haileyPhoneLabel: "(877) 764-9917",
  shows: [
    { eventId: "3404870", day: "Fri, Oct 23", time: "8:00 PM", endsAt: "2026-10-24T06:00:00Z" },
    { eventId: "3451126", day: "Sat, Oct 24", time: "8:00 PM", endsAt: "2026-10-25T06:00:00Z" },
  ],
};

const PAID_PARAMS = ["ttclid", "fbclid", "gclid", "gbraid", "wbraid"];
const PAID_SOURCES = /^(tiktok|facebook|fb|meta|instagram|ig|youtube|google_ads|google-ads)$/i;
const PAID_MEDIUMS = /^(paid|paid_social|paidsocial|cpc|cpm|ppc|social_paid|video|display)$/i;
const SESSION_KEY = "instapass_ad_landing";
const DISMISSED_KEY = "instapass_ad_popup_closed";

/** True when this visit came from an ad. Remembered for the session, so Home → back shows it again. */
export function isPaidVisit(search = typeof window !== "undefined" ? window.location.search : "") {
  try {
    const q = new URLSearchParams(search);
    const paid =
      PAID_PARAMS.some((p) => q.get(p)) ||
      PAID_SOURCES.test(q.get("utm_source") || "") ||
      PAID_MEDIUMS.test(q.get("utm_medium") || "");
    if (paid) {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* private mode */
      }
      return true;
    }
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function campaignIsLive(now = Date.now()) {
  return CAMPAIGN.shows.some((s) => Date.parse(s.endsAt) > now);
}

function useFromPrices() {
  const [prices, setPrices] = useState({});
  useEffect(() => {
    let cancelled = false;
    CAMPAIGN.shows.forEach((s) => {
      axios
        .get(`${ApiURL}/transcation/price-summary/${s.eventId}`, { timeout: 8000 })
        .then(({ data }) => {
          if (!cancelled && data?.minPrice) setPrices((p) => ({ ...p, [s.eventId]: data }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return prices;
}

export default function AdLanding({ onPick } = {}) {
  const prices = useFromPrices();
  const chat = useInstaPassAIChat();
  const [copied, setCopied] = useState(false);
  const shows = CAMPAIGN.shows.filter((s) => Date.parse(s.endsAt) > Date.now());
  const lowest = Math.min(...shows.map((s) => prices[s.eventId]?.minPrice || Infinity));

  useEffect(() => {
    trackEvent("ad_landing_view", { campaign: CAMPAIGN.key });
    trackMeta("ViewContent", { content_name: `${CAMPAIGN.artist} at ${CAMPAIGN.venue}`, content_type: "product" });
  }, []);

  const copyPromo = () => {
    trackEvent("ad_landing_promo_copy", { campaign: CAMPAIGN.key });
    navigator.clipboard?.writeText(CAMPAIGN.promo).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {}
    );
  };

  return (
    <section className="bg-[#0d0d0d] text-white" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <div className="max-w-[560px] mx-auto">
        <div className="relative">
          <img
            src={CAMPAIGN.poster}
            alt={`${CAMPAIGN.artist} live at ${CAMPAIGN.venue}`}
            width={760}
            height={670}
            fetchpriority="high"
            className="w-full h-auto block"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0d0d0d] to-transparent" />
        </div>

        <div className="px-4 -mt-6 relative">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#E52324]">Live · 2 nights only</p>
          <h1 className="text-[30px] leading-[1.05] font-black uppercase mt-1">
            {CAMPAIGN.artist} <span className="text-white/60">at</span> {CAMPAIGN.venue}
          </h1>
          <p className="text-sm text-white/60 mt-1">
            {CAMPAIGN.city}
            {Number.isFinite(lowest) ? (
              <>
                {" "}
                · Tickets from <span className="text-white font-bold">${lowest}</span>
              </>
            ) : null}
          </p>

          <div className="mt-4 space-y-2.5">
            {shows.map((s) => {
              const p = prices[s.eventId];
              return (
                <Link
                  key={s.eventId}
                  to={`/tickets/group.details/${s.eventId}`}
                  onClick={() => {
                    trackEvent("ad_landing_pick_show", { campaign: CAMPAIGN.key, event_id: s.eventId });
                    onPick?.();
                  }}
                  className="flex items-center justify-between gap-3 rounded-2xl bg-[#E52324] hover:bg-[#c91f20] active:bg-[#b51b1c] px-4 py-3.5 transition-colors"
                >
                  <span>
                    <span className="block text-lg font-black leading-tight">{s.day}</span>
                    <span className="block text-xs text-white/80">
                      {s.time}
                      {p?.listings ? ` · ${p.listings.toLocaleString()} listings` : ""}
                    </span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-[10px] uppercase tracking-wider text-white/80">From</span>
                    <span className="block text-xl font-black leading-tight">{p?.minPrice ? `$${p.minPrice}` : "…"}</span>
                  </span>
                </Link>
              );
            })}
          </div>

          <button
            type="button"
            onClick={copyPromo}
            className="mt-3 w-full flex items-center justify-between rounded-2xl border border-dashed border-[#F5C542]/60 bg-[#F5C542]/10 px-4 py-3 text-left"
          >
            <span>
              <span className="block text-[11px] uppercase tracking-wider text-[#F5C542]">Promo code · {CAMPAIGN.promoDiscount}</span>
              <span className="block text-lg font-black tracking-wide">{CAMPAIGN.promo}</span>
            </span>
            <span className="text-xs font-bold text-[#F5C542]">{copied ? "Copied" : "Tap to copy"}</span>
          </button>

          <div className="mt-3 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => {
                trackEvent("ad_landing_hailey_chat", { campaign: CAMPAIGN.key });
                onPick?.();
                chat?.setOpen?.(true);
              }}
              className="rounded-2xl bg-white/10 hover:bg-white/15 px-3 py-3 text-left"
            >
              <span className="block text-sm font-bold">Ask Hailey</span>
              <span className="block text-[11px] text-white/60">Best seats, parking, hotels</span>
            </button>
            <a
              href={`tel:${CAMPAIGN.haileyPhone}`}
              onClick={() => trackEvent("ad_landing_hailey_call", { campaign: CAMPAIGN.key })}
              className="rounded-2xl bg-white/10 hover:bg-white/15 px-3 py-3"
            >
              <span className="block text-sm font-bold">Call Hailey</span>
              <span className="block text-[11px] text-white/60">{CAMPAIGN.haileyPhoneLabel}</span>
            </a>
          </div>

          <p className="text-[11px] text-white/40 text-center mt-4 pb-6">
            Every ticket 100% guaranteed · Instant delivery on eligible tickets
          </p>
        </div>
      </div>
    </section>
  );
}

function popupWasClosed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The campaign screen as a pop-up over the normal homepage. Shown to ad
 * visitors once per browser session while the campaign is live; closing it
 * (X, tapping outside, Esc) or picking a show leaves the regular hero.
 */
export function AdLandingPopup() {
  const [open, setOpen] = useState(() => isPaidVisit() && campaignIsLive() && !popupWasClosed());

  const close = useCallback((how) => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* private mode */
    }
    if (how) trackEvent("ad_landing_close", { campaign: CAMPAIGN.key, how });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close("esc");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${CAMPAIGN.artist} at ${CAMPAIGN.venue}`}
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && close("backdrop")}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6" onClick={(e) => e.target === e.currentTarget && close("backdrop")}>
        <div className="relative w-full max-w-[460px] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
          <button
            type="button"
            onClick={() => close("x")}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/60 text-white text-xl leading-none flex items-center justify-center hover:bg-black/80"
          >
            ×
          </button>
          <AdLanding onPick={() => close()} />
        </div>
      </div>
    </div>
  );
}
